import * as i0 from '@angular/core';
import { Injectable, Inject, EventEmitter, Optional, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { map, catchError, throwError, filter } from 'rxjs';
import * as i1$1 from '@angular/router';
import { NavigationEnd } from '@angular/router';
import merge from 'lodash-es/merge';
import * as i2 from '@wdpr/ra-angular-logger';
import { LoggerModule } from '@wdpr/ra-angular-logger';
import * as i1 from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';

/**
 * Service to retrieve data from the PAGE_KEY_URL_ENDPOINT provided in the app module.
 */
class PageKeyServerDataService {
    constructor(http, pageKeyEndpointUrl) {
        this.http = http;
        this.pageKeyEndpointUrl = pageKeyEndpointUrl;
    }
    getData() {
        return this.http.get(this.pageKeyEndpointUrl)
            .pipe(map((response) => {
            return response;
        }), catchError(this.handleAndThrowRemoteError.bind(this)));
    }
    handleAndThrowRemoteError(error) {
        let errorMessage = 'An error occurred: ';
        // in a real world app, we may send the server to some remote logging infrastructure
        // instead of just logging it to the console
        if (error.error instanceof ErrorEvent) {
            // A client-side or network error occurred. Handle it accordingly.
            errorMessage += `An error occurred at client: ${error.error.message}`;
        }
        else {
            // Server-Side Error: The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong,
            if (error.error && error.error.status) {
                errorMessage += `Backend service returns error - ${error.error.status}: ${error.error.message}`;
            }
            else {
                errorMessage += `Backend service returns error - ${error.status}: ${error.message}`;
            }
        }
        return throwError(() => errorMessage);
    }
}
PageKeyServerDataService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: PageKeyServerDataService, deps: [{ token: i1.HttpClient }, { token: 'PAGE_KEY_URL_ENDPOINT' }], target: i0.ɵɵFactoryTarget.Injectable });
PageKeyServerDataService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: PageKeyServerDataService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: PageKeyServerDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['PAGE_KEY_URL_ENDPOINT']
                }] }]; } });

/**
 * Service used to generate page keys according to the configurations set by the user in their
 * app module.
 */
class PageKeyService extends EventEmitter {
    constructor(router, logger, additionalConfig, template, remoteCallFlag, pageKeyServerDataService) {
        super() /* istanbul ignore next */;
        this.router = router;
        this.logger = logger;
        this.additionalConfig = additionalConfig;
        this.template = template;
        this.remoteCallFlag = remoteCallFlag;
        this.pageKeyServerDataService = pageKeyServerDataService;
        /**
         * If flag is set to true, a request will be made to the PAGE_KEY_URL_ENDPOINT to retrieve
         * fresh configuration data.  If the flag is set to false, the configuration data will be
         * retrieved from the dataSubscription member variable.  The flag is set to false after
         * each request by default.
         */
        this.flag = this.remoteCallFlag || true;
        this.init();
        this.flag = true;
    }
    emit(value) {
        super.next(value);
    }
    /**
     * Initializes the component and begins the routeChangeListener().
     */
    init() {
        this.routeChangeListener();
    }
    /**
     * Listener to detect every time a route changes.  If the change is an instance of a NavigationEnd
     * event, the page key will be dynamically generated from the configurations provided in the app module.
     */
    routeChangeListener() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd), catchError((error) => {
            return throwError(() => error);
        })).subscribe((event) => {
            if (this.flag) {
                return this.dataSubscription = this.pageKeyServerDataService.getData()
                    .subscribe((data) => {
                    // Check for a bad 200 response.
                    if (data.code && data.code >= 400) {
                        this.logger.error('api endpoint not configured properly');
                        return;
                    }
                    this.additionalConfig = merge(this.additionalConfig, data);
                    this.configuration(event);
                    if (!this.remoteCallFlag) {
                        this.flag = false;
                    }
                });
            }
            else {
                this.configuration(event);
                if (!this.remoteCallFlag) {
                    this.flag = false;
                }
                if (this.dataSubscription) {
                    this.dataSubscription.unsubscribe();
                }
            }
        });
    }
    configuration(event) {
        const eventData = {
            event: event,
            protocol: window.location.protocol,
            host: window.location.hostname,
            port: window.location.port,
            url: event.url,
            search: window.location.search,
            hash: window.location.hash
        };
        const pageKey = this.processTemplate(eventData);
        this.emit({ key: pageKey });
    }
    replaceSlash(value) {
        let pageKey = value.replace('^\/', ''); // remove start slash
        pageKey = pageKey.replace(/\/$/, ''); // remove trailing slash
        pageKey = pageKey.replace(/#.*/g, ''); // remove fragment
        pageKey = pageKey.replace(/\?.*/, ''); // remove query params
        pageKey = pageKey.replace(/\//g, '.'); // replace `/` with `.`
        pageKey = pageKey.replace(/\.$/g, ''); // remove the trailing `.`
        return pageKey;
    }
    processTemplate(values) {
        const config = merge(this.additionalConfig, values);
        return this.createTemplate(config);
    }
    createTemplate(config) {
        return this.template
            .map((key) => {
            if (key && key !== '') {
                const value = config[`${key}`];
                if (value && value !== '') {
                    return this.replaceSlash(value);
                }
            }
            this.logger.log(`key: ${key} is not set, Look at config options of PAGE_KEY_ADDITIONAL_CONFIG`);
            return key;
        })
            .join('.')
            .replace(/\.\./g, '.'); // catch call `..` to ``
    }
}
PageKeyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: PageKeyService, deps: [{ token: i1$1.Router }, { token: i2.LoggerService }, { token: 'PAGE_KEY_ADDITIONAL_CONFIG' }, { token: 'PAGE_KEY_TEMPLATE' }, { token: 'PAGE_KEY_REMOTE_CALL_FLAG', optional: true }, { token: PageKeyServerDataService }], target: i0.ɵɵFactoryTarget.Injectable });
PageKeyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: PageKeyService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: PageKeyService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Router }, { type: i2.LoggerService }, { type: Object, decorators: [{
                    type: Inject,
                    args: ['PAGE_KEY_ADDITIONAL_CONFIG']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['PAGE_KEY_TEMPLATE']
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: ['PAGE_KEY_REMOTE_CALL_FLAG']
                }] }, { type: PageKeyServerDataService }]; } });

class PageKeyModule {
}
PageKeyModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: PageKeyModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
PageKeyModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.1.5", ngImport: i0, type: PageKeyModule, imports: [CommonModule,
        LoggerModule,
        HttpClientModule] });
PageKeyModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: PageKeyModule, providers: [
        {
            provide: 'PAGE_KEY_ADDITIONAL_CONFIG',
            useValue: {}
        },
        {
            provide: 'PAGE_KEY_TEMPLATE',
            useValue: []
        },
        {
            provide: 'PAGE_KEY_URL_ENDPOINT',
            useValue: '/api/page-key-data'
        },
        {
            provide: 'PAGE_KEY_REMOTE_CALL_FLAG',
            useValue: true
        },
        PageKeyService,
        PageKeyServerDataService
    ], imports: [CommonModule,
        LoggerModule,
        HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.1.5", ngImport: i0, type: PageKeyModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        LoggerModule,
                        HttpClientModule
                    ],
                    declarations: [],
                    exports: [],
                    providers: [
                        {
                            provide: 'PAGE_KEY_ADDITIONAL_CONFIG',
                            useValue: {}
                        },
                        {
                            provide: 'PAGE_KEY_TEMPLATE',
                            useValue: []
                        },
                        {
                            provide: 'PAGE_KEY_URL_ENDPOINT',
                            useValue: '/api/page-key-data'
                        },
                        {
                            provide: 'PAGE_KEY_REMOTE_CALL_FLAG',
                            useValue: true
                        },
                        PageKeyService,
                        PageKeyServerDataService
                    ]
                }]
        }] });

// All modules should be listed here

/**
 * Generated bundle index. Do not edit.
 */

export { PageKeyModule, PageKeyServerDataService, PageKeyService };
